import React from "react";
import type { ToastPosition } from "react-hot-toast";
import toast, { resolveValue, useToaster } from "react-hot-toast";

import { cn } from "~/components/ui/cn";
import { CheckFilledCircleIcon } from "~/components/ui/icons/check-filled-circle";
import { CloseFilledCircleIcon } from "~/components/ui/icons/close-filled-circle";
import { InformationIcon } from "~/components/ui/icons/information";

export type ToastProps = {
  /**
   * Define the position of the toast container: https://react-hot-toast.com/docs/toaster#position-prop
   */
  position?: ToastPosition;
  /**
   * Define the order of spawning of new toasts: https://react-hot-toast.com/docs/toaster#reverseorder-prop
   */
  reverseOrder?: boolean;
  /**
   * Define the gap between each toast: https://react-hot-toast.com/docs/toaster#gutter-prop
   */
  gutter?: number;
  /**
   * Define custom classnames for the toast container
   */
  className?: string;
};

// https://github.com/timolins/react-hot-toast/blob/d208114c9658485d999890575eb08d3c9cbf58cc/src/components/toaster.tsx#L16-L43
const getPositionClasses = (position: ToastPosition): string => {
  const top = position.includes("top");
  const verticalClass = top ? "top-0" : "bottom-0";
  const horizontalClass = position.includes("center")
    ? "justify-center"
    : position.includes("right")
      ? "justify-end"
      : "";
  return [
    "left-0",
    "right-0",
    "flex",
    "absolute",
    verticalClass,
    horizontalClass,
  ].join(" ");
};

// https://github.com/timolins/react-hot-toast/blob/d208114c9658485d999890575eb08d3c9cbf58cc/src/components/toaster.tsx#L16-L43
const getPositionStyle = (
  position: ToastPosition,
  offset: number,
): React.CSSProperties => {
  const top = position.includes("top");
  return {
    transition: `all 230ms cubic-bezier(.21,1.02,.73,1)`,
    transform: `translateY(${offset * (top ? 1 : -1)}px)`,
  };
};

// https://github.com/timolins/react-hot-toast/blob/d208114c9658485d999890575eb08d3c9cbf58cc/src/core/utils.ts#L8-L17
export const createRectRef =
  (onRect: (rect: DOMRect) => void) =>
  (el: HTMLElement | null): void => {
    if (el) {
      setTimeout(() => {
        const boundingRect = el.getBoundingClientRect();
        onRect(boundingRect);
      });
    }
  };

/**
 * Main toast component.
 */
export const Toast: React.FC<ToastProps> = ({
  position = "top-center",
  reverseOrder = false,
  gutter = 8,
  ...props
}: ToastProps) => {
  const { toasts, handlers } = useToaster();
  const { startPause, endPause, calculateOffset, updateHeight } = handlers;

  return (
    <div
      onMouseEnter={startPause}
      onMouseLeave={endPause}
      className="pointer-events-none fixed inset-10 z-50"
      {...props}
    >
      {toasts.map((toast) => {
        const toastPosition = toast.position ?? position;
        const offset = calculateOffset(toast, {
          reverseOrder,
          gutter,
          defaultPosition: position,
        });

        const ref = toast.height
          ? undefined
          : createRectRef((rect) => {
              updateHeight(toast.id, rect.height);
            });

        return (
          <div
            key={toast.id}
            ref={ref}
            style={{
              opacity: toast.visible ? 1 : 0,
              ...getPositionStyle(toastPosition, offset),
            }}
            className={getPositionClasses(toastPosition)}
          >
            {/*ToastBar*/}
            {toast.type === "custom" ? (
              resolveValue(toast.message, toast)
            ) : (
              <div
                className={cn(
                  "pointer-events-auto z-9999 flex max-w-[600px] rounded-lg px-4 py-2 text-sm font-semibold",
                  {
                    "bg-[#ECF7F1] text-green-600": toast.type === "success",
                    "bg-red-300 text-red-600": toast.type === "error",
                    "bg-gray-100":
                      toast.type === "blank" || toast.type === "loading",
                  },
                  toast.visible ? "animate-enter" : "animate-leave",
                )}
              >
                <div
                  className="flex flex-auto items-center justify-center gap-x-2 break-words"
                  style={{ overflowWrap: "anywhere" }}
                  {...toast.ariaProps}
                >
                  <div>
                    {
                      {
                        success: <CheckFilledCircleIcon className="size-4" />,
                        error: <CloseFilledCircleIcon className="size-4" />,
                        blank: <InformationIcon className="size-4" />,
                        loading: <InformationIcon className="size-4" />,
                      }[toast.type]
                    }
                  </div>
                  {resolveValue(toast.message, toast)}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

Toast.displayName = "Toast";

export { toast };
